import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { MARKETS } from '../mock';
import { CITIES } from '../data/cities';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { PAGE_SEO, SITE_CONFIG } from '../data/seoConfig';

export default function Locations() {
  useSEO(PAGE_SEO.locations);

  useJsonLd('locations-breadcrumb', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
      { '@type': 'ListItem', position: 2, name: 'Locations', item: `${SITE_CONFIG.url}/locations` },
    ],
  });

  const byCountry = MARKETS.reduce((acc, m) => {
    acc[m.slug] = { market: m, cities: CITIES.filter((c) => c.countrySlug === m.slug) };
    return acc;
  }, {});

  return (
    <div>
      <PageHeader
        eyebrow='Locations'
        title={<><span className='text-white'>Local expertise </span><span className='text-brand-gradient'>where you grow</span></>}
        description="City-specific growth programs across 25+ top markets. Each Ransen city hub delivers localized strategy, native creative, and reporting on your time zone."
        crumbs={[{ label: 'Locations' }]}
      />

      {/* COUNTRIES */}
      <section className='py-16'>
        <div className='max-w-6xl mx-auto px-6'>
          <h2 className='font-heading text-3xl md:text-4xl font-bold text-white mb-8'>Countries</h2>
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'>
            {MARKETS.map((m) => (
              <Link key={m.slug} to={`/markets/${m.slug}`} className='glass-card rounded-2xl p-5 group'>
                <div className='flex items-center gap-3'>
                  <div className='text-3xl'>{m.flag}</div>
                  <div>
                    <div className='font-heading font-bold text-white text-[15px]'>{m.name}</div>
                    <div className='text-brand text-[11px] tracking-wider uppercase mt-0.5 font-accent'>{m.region}</div>
                  </div>
                </div>
                <div className='mt-3 text-white/55 text-[12.5px]'>{m.tag}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES BY COUNTRY */}
      <section className='py-16 border-t border-brand/10'>
        <div className='max-w-6xl mx-auto px-6'>
          <h2 className='font-heading text-3xl md:text-4xl font-bold text-white mb-3'>City Hubs</h2>
          <p className='text-white/60 max-w-2xl font-body mb-10'>Each city hub is a dedicated landing page with local market context, local challenges, local FAQs, and city-specific content — designed to serve both search engines and local decision-makers.</p>

          <div className='space-y-14'>
            {Object.values(byCountry).filter((g) => g.cities.length > 0).map(({ market, cities }) => (
              <div key={market.slug}>
                <div className='flex items-center gap-3 mb-6'>
                  <div className='text-3xl'>{market.flag}</div>
                  <div>
                    <Link to={`/markets/${market.slug}`} className='font-heading font-bold text-white text-xl hover:text-brand transition-colors'>{market.name}</Link>
                    <div className='text-brand text-[11px] tracking-wider uppercase font-accent'>{market.region} · {cities.length} city hub{cities.length > 1 ? 's' : ''}</div>
                  </div>
                </div>
                <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'>
                  {cities.map((c) => (
                    <Link key={c.slug} to={`/locations/${c.slug}`} className='glass-card rounded-2xl p-5 group'>
                      <div className='flex items-center gap-2 text-brand'>
                        <MapPin className='w-4 h-4' />
                        <div className='text-[10.5px] tracking-wider uppercase font-accent'>City Hub</div>
                      </div>
                      <div className='mt-3 font-heading font-bold text-white text-[16px]'>{c.name}</div>
                      <div className='mt-1 flex items-center gap-1.5 text-white/50 text-[12.5px]'>
                        <Users className='w-3 h-3' /> {c.population}
                      </div>
                      <div className='mt-4 inline-flex items-center gap-1.5 text-brand text-[12.5px] font-medium group-hover:gap-2.5 transition-all'>
                        Explore <ArrowRight className='w-3 h-3' />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className='py-20 border-t border-brand/10 text-center'>
        <div className='max-w-3xl mx-auto px-6'>
          <h3 className='font-heading text-3xl md:text-4xl font-bold text-white'>Don&rsquo;t see your city?</h3>
          <p className='mt-4 text-white/65 font-body'>We work with brands across 38+ countries. Reach out to discuss your market.</p>
          <Link to='/contact' className='mt-8 btn-primary text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2'>
            Book a Strategy Call <ArrowRight className='w-4 h-4' />
          </Link>
        </div>
      </section>
    </div>
  );
}
